import Vue from "vue";
import Vuex from "vuex";
import createPersistedState from "vuex-persistedstate";
import createMutationsSharer from "vuex-shared-mutations";
import { setClientLanguage } from "../lang/i18n";
import { VersionCheckService } from "../services/VersionCheckService";

// Modules import
import departments from "./modules/departments";
import customer from "./modules/customer";
import cachemod from "./modules/cachemod";
import shipment from "./modules/shipment";
import { CurrentLogin, LegalEntity, UserDef } from "../model/component.model";
import createCache from "vuex-cache";
import { getTenantId } from "@/services/AuthService";

Vue.use(Vuex);

const hostsDevelopment: string[] = ["www.online.dev.mbehub.cloud.mbeglobal.io"];
const hostsLocal: string[] = ["localhost", "127.0.0.1"];
const hostsIntegration: string[] = [
  "www.demo.mbeonline.eu",
  "www.demo.mbeonline.it"
];
const hostsEShip: string[] = [
  "www-online-eship.apps.rosa-devint.62tr.p1.openshiftapps.com"
];
const hostsPreProduction: string[] = [
  "www-online-preprod.apps.hub-devint.ocp.mbeglobal.io"
];
const hostsProd: string[] = [
  "www.mbeonline.it",
  "www.mbeonline.eu",
  "www.mbeonline.fr",
  "www.mbeonline.es",
  "www.mbeonline.fr",
  "www.mbeonline.pl",
  "www.mbeonline.pt",
  "www.mbeonline.ch",
  "www.mbeonline.de",
  "www.mbeonline.hr",
  "www.mbeonline.at"
];

const store = new Vuex.Store({
  plugins: [
    createCache(),
    createMutationsSharer({ predicate: ["updateRemoteVersion"] }),
    // Plugin to persist the state on the refresh page
    createPersistedState({
      key: "mol-vuex",
      paths: [
        "customer",
        "currency",
        "departments",
        "businessName",
        "saveNotice",
        "user",
        "username",
        "userEmail",
        "userStores",
        "storeSelected",
        "multiStoreSelected",
        "language",
        "hashVersion",
        "timeZoneId",
        "impersonator",
        "firstImpersonator",
        "BASE_API_URL",
        "BASE_HUB_URL",
        "KEYCLOAK_BASE_API_URL",
        "versionCheckTimestamp",
        "BASE_QAPLA_URL",
        "qapla",
        "storePaperlessEnabled",
        "storeCourierPickupsManagementEnabled",
        "storeAutopilotEnabled",
        "wineVisibility",
        "printOnlineEnabled",
        "printOnlineStoreUrl"
      ]
    })
  ],
  modules: {
    departments,
    customer,
    cachemod,
    shipment
  },
  state: {
    // banners: JSON.parse(JSON.stringify(require("../assets/banners.json"))),
    banners: JSON.parse(
      JSON.stringify(require("../assets/responsive-banners.json"))
    ),
    businessName: "",
    saveNotice: "",
    user: null,
    impersonator: null,
    firstImpersonator: null,
    username: "",
    userEmail: "",
    userStores: "",
    currency: "",
    multiStoreSelected: "",
    storeSelected: "",
    language: "",
    languages: ["ca", "de", "en", "es", "fr", "it", "pl", "pt", "hr"],
    hashVersion: "",
    timeZoneId: "",
    BASE_API_URL: process.env.VUE_APP_BASE_API_URL,
    KEYCLOAK_BASE_API_URL: "",
    BASE_HUB_URL: "",
    BASE_QAPLA_URL: "",
    versionCheckTimestamp: undefined,
    appVersion: "",
    remoteVersion: "",
    currentLogin: {
      initialized: false,
      isItaly: false,
      isSpain: false,
      isGermany: false,
      isAustria: false,
      isFrance: false,
      isPoland: false,
      isPortugal: false,
      isSwitzerland: false,
      isCroatia: false,
      isUk: false
    },
    layout: "",
    qapla: {
      qaplaEnabled: false,
      qaplaLevel: "",
      customerQaplaEnabled: false,
      storeQaplaEnabled: false
    },
    storePaperlessEnabled: false,
    storeCourierPickupsManagementEnabled: false,
    storeAutopilotEnabled: false,
    wineVisibility: false
  },
  getters: {
    banners: (state: any) => {
      return state.banners;
    },
    currency: (state: any) => {
      return state.currency;
    },
    businessName: (state: any) => {
      return state.businessName;
    },
    customer: (state: any) => {
      return state.customer;
    },
    saveNotice: (state: any) => {
      return state.saveNotice;
    },
    impersonator: (state: any) => {
      return state.impersonator;
    },
    firstImpersonator: (state: any) => {
      return state.firstImpersonator;
    },
    user: (state: any) => {
      return state.user;
    },
    username: (state: any) => {
      return state.username;
    },
    userEmail: (state: any) => {
      return state.userEmail;
    },
    userStores: (state: any) => {
      return state.userStores;
    },
    storeSelected: (state: any) => {
      return state.storeSelected;
    },
    multiStoreSelected: (state: any) => {
      return state.multiStoreSelected;
    },
    language: (state: any) => {
      return state.language;
    },
    taxDutyEnabled: (state: any, getters: any) => {
      return true;
    },
    BASE_API_URL: (state: any) => {
      return state.BASE_API_URL;
    },
    BASE_HUB_URL: (state: any) => {
      return state.BASE_HUB_URL;
    },
    KEYCLOAK_BASE_API_URL: (state: any) => {
      return state.KEYCLOAK_BASE_API_URL;
    },
    BASE_QAPLA_URL: (state: any) => {
      return state.BASE_QAPLA_URL;
    },
    hashVersion: (state: any) => {
      return state.hashVersion;
    },
    timeZoneId: (state: any) => {
      return state.timeZoneId;
    },
    versionCheckTimestamp: (state: any) => {
      return state.versionCheckTimestamp;
    },
    envs: (state: any) => {
      const envs = {
        isLocal: hostsLocal.includes(window.location.hostname),
        isDevelopment: hostsDevelopment.includes(window.location.hostname),
        isIntegration: hostsIntegration.includes(window.location.hostname),
        isEShip: hostsEShip.includes(window.location.hostname),
        isPreProduction: hostsPreProduction.includes(window.location.hostname),
        isProduction: hostsProd.includes(window.location.hostname),
        isDev: false,
        envLabel: ""
      };
      let label = "";
      envs.isDev = envs.isLocal || envs.isDevelopment || envs.isIntegration;
      if (envs.isDev) {
        label = "[DEV environment]";
      }
      if (envs.isLocal) {
        label = "[local development]";
      }
      if (envs.isIntegration) {
        label = "[DEMO Server]";
      }
      if (envs.isPreProduction) {
        label = "[Pre-PROD environment]";
      }
      envs.envLabel = label;
      return envs;
    },
    appVersion: (state: any) => {
      return process.env.VUE_APP_VERSION;
    },
    reloadAppTab: (state: any, getters: any) => {
      if (state.remoteVersion === "") {
        return false;
      }
      return getters.appVersion !== state.remoteVersion;
    },
    features: (state: any, getters: any) => {
      const current = new Date().getTime();
      const features = {
        qapla: {
          qaplaEnabled: state.qapla.qaplaEnabled,
          qaplaLevel: state.qapla.qaplaLevel,
          customerQaplaEnabled: state.qapla.customerQaplaEnabled,
          storeQaplaEnabled: state.qapla.storeQaplaEnabled
        },
        inbound: getters["customer/config"].canSelectSShipmentType,
        excel_import: getters.envs.isDev || getters.envs.isPreProduction,
        new_manual: true,
        apikeyv2: false,
        storePaperlessEnabled: state.storePaperlessEnabled,
        storeCourierPickupsManagementEnabled: true,
        storeAutopilotEnabled: state.storeAutopilotEnabled,
        wineVisibilty: state.wineVisibility,
        printOnlineEnabled: state.printOnlineEnabled,
        printOnlineStoreUrl: state.printOnlineStoreUrl
      };
      if (getters.envs.isProduction) {
        features.excel_import = true;
      }
      const startApiKeyV2 = 1657663200000; //13 july 2022
      features.apikeyv2 =
        !getters.envs.isProduction ||
        getters.user.username === "vmerluzzimolprod1.it0001.mol" ||
        current >= startApiKeyV2;
      return features;
    }
  },
  actions: {
    versionCheck() {
      VersionCheckService.prototype.checkVersion();
    },
    updateLanguage({ commit, state }, { lang }) {
      setClientLanguage(lang as string);
      commit("updateLanguage", lang as string);
    },
    afterTokenStored({ commit }) {
      //called after a valid login token is set / update
      const tenantId = getTenantId();
      const currentLogin = {
        initialized: true,
        isItaly: tenantId === 1,
        isSpain: tenantId === 2,
        isGermany: tenantId === 3,
        isAustria: tenantId === 4,
        isFrance: tenantId === 5,
        isPoland: tenantId === 6,
        isPortugal: tenantId === 7,
        isSwitzerland: tenantId === 8,
        isCroatia: tenantId === 9,
        isUk: tenantId === 15
      } as CurrentLogin;
      commit("currentLogin", currentLogin);
    }
  },
  mutations: {
    setCurrency: (state: any, currency: string) => {
      state.currency = currency;
    },
    updateBusinessName: (state: any, businessName: string) => {
      state.businessName = businessName;
    },
    updateSaveNotice: (state: any, saveNotice: string) => {
      state.saveNotice = saveNotice;
    },
    updateUser: (state: any, user: UserDef) => {
      state.user = user;
    },
    updateUsername: (state: any, username: string) => {
      state.username = username;
    },
    updateUserEmail: (state: any, userEmail: string) => {
      state.userEmail = userEmail;
    },
    updateUserStores: (state: any, userStores: LegalEntity[]) => {
      state.userStores = userStores;
    },
    updateStoreSelected: (state: any, storeSelected: LegalEntity) => {
      state.storeSelected = storeSelected;
    },
    updateMultiStoreSelected: (state: any, multiStoreSelected: any) => {
      state.multiStoreSelected = multiStoreSelected;
    },
    updateLanguage: (state: any, language: string) => {
      state.language = language;
    },
    updateBASE_API_URL: (state: any, url: string) => {
      state.BASE_API_URL = url;
    },
    updateBASE_HUB_URL: (state: any, url: string) => {
      state.BASE_HUB_URL = url;
    },
    updateKEYCLOAK_BASE_API_URL: (state: any, url: string) => {
      state.KEYCLOAK_BASE_API_URL = url;
    },
    updateBASE_QAPLA_URL: (state: any, url: string) => {
      state.BASE_QAPLA_URL = url;
    },
    updateHashVersion: (state: any, hashVersion: string) => {
      state.hashVersion = hashVersion;
    },
    updateStoreTimeZoneId: (state: any, timeZoneId: any) => {
      state.timeZoneId = timeZoneId;
    },
    versionCheckTimestamp: (state: any, versionCheckTimestamp: boolean) => {
      state.versionCheckTimestamp = versionCheckTimestamp;
    },
    setAppVersion: (state: any, appVersion: string) => {
      state.appVersion = appVersion;
    },
    setImpersonator(state: any, impersonator: string | null) {
      state.impersonator = impersonator;
    },
    setFirstImpersonator(state: any, impersonator: string | null) {
      state.firstImpersonator = impersonator;
    },
    updateRemoteVersion: (state: any, remoteVersion: string) => {
      state.remoteVersion = remoteVersion;
    },
    currentLogin: (state: any, currentLogin: CurrentLogin) => {
      state.currentLogin = currentLogin;
    },
    setLayout: (state: any, layout: string) => {
      state.layout = layout;
    },
    updateQaplaConfig: (state: any, qaplaConfig: any) => {
      state.qapla.qaplaEnabled = qaplaConfig.qaplaEnabled;
      state.qapla.qaplaLevel = qaplaConfig.qaplaLevel;
    },
    updateCustomerQaplaEnabled: (state: any, customerQaplaEnabled: boolean) => {
      state.qapla.customerQaplaEnabled = customerQaplaEnabled;
    },
    updateStoreQaplaEnabled: (state: any, storeQaplaEnabled: boolean) => {
      state.qapla.storeQaplaEnabled = storeQaplaEnabled;
    },
    updateStorePaperlessEnabled: (
      state: any,
      storePaperlessEnabled: boolean
    ) => {
      state.storePaperlessEnabled = storePaperlessEnabled;
    },
    updateStoreCourierPickupsManagementEnabled: (
      state: any,
      storeCourierPickupsManagementEnabled: boolean
    ) => {
      state.storeCourierPickupsManagementEnabled = storeCourierPickupsManagementEnabled;
    },
    updateStoreAutopilotEnabled: (
      state: any,
      storeAutopilotEnabled: boolean
    ) => {
      state.storeAutopilotEnabled = storeAutopilotEnabled;
    },
    updateWineVisibilty: (state: any, storeWineVisibility: boolean) => {
      state.wineVisibility = storeWineVisibility;
    },
    updatePrintOnlineEnabled: (state: any, printOnlineEnabled: boolean) => {
      state.printOnlineEnabled = printOnlineEnabled;
    },
    updatePrintOnlineStoreUrl: (state: any, printOnlineStoreUrl: string) => {
      state.printOnlineStoreUrl = printOnlineStoreUrl;
    }
  }
});

export default store;
